package com.example.mystybag.common.logic

import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.BottomSheetChild
import com.example.mystybag.core.logic.base.DialogConfig
import com.example.mystybag.core.logic.base.NavigatorConfig
import com.example.mystybag.core.logic.base.UIBottomSheetConfig
import com.example.mystybag.core.model.ColoredText
import com.example.mystybag.core.model.LinkModel
import com.example.mystybag.common.model.ChooseTakePhotoOrPickFileVisualModel
import com.example.mystybag.common.model.FilterBottomSheetVisualModel
import com.example.mystybag.common.model.DualTextInputBSVisualModel
import com.example.mystybag.common.model.TextInputBSVisualModel
import com.example.mystybag.sharedmodels.common.responseModels.CountryModel
import com.example.mystybag.sharedmodels.mobileAdmin.database.UsersCredentialTypes

data class NavigatorChooseCountry(
    val countriesType       : Int,
    val selectedCountry     : List<CountryModel>    = listOf(),
    val excludedCountries   : List<String>          = listOf(),
    val showSearchRightAway : Boolean               = false,
    val onSuccess           : (List<CountryModel>) -> Unit
): NavigatorConfig()

object NavigatorInternetTroubleshooting: NavigatorConfig()

object NavigatorEnterMailAddress: NavigatorConfig()
object NavigatorMerchantLogin: NavigatorConfig()
/** When coming from successful merchant login: pass session so biometric screen does not depend on Preferences/isJwtExpired. */
data class NavigatorMerchantBiometric(
    val passwordToSaveAfterBiometric: String? = null,
    val jwt: String = "",
    val merchantId: Long = 0L
): NavigatorConfig()
object NavigatorMerchantForgotPasswordEmail: NavigatorConfig()
data class NavigatorMerchantResetPassword(val email: String, val temporaryPassword: String = ""): NavigatorConfig()

// Staff (employee) login flow
object NavigatorStaffLogin: NavigatorConfig()
/** When coming from successful staff login: pass session so biometric screen does not depend on Preferences. */
data class NavigatorStaffBiometric(
    val passwordToSaveAfterBiometric: String? = null,
    val jwt: String = "",
    val merchantId: Long = 0L,
    val staffLocationIds: String = "" // comma-separated, for PREFERENCES_STAFF_LOCATION_IDS
): NavigatorConfig()
object NavigatorStaffForgotPasswordEmail: NavigatorConfig()
data class NavigatorStaffResetPassword(val email: String, val temporaryPassword: String = ""): NavigatorConfig()
object NavigatorStaffChangePassword: NavigatorConfig()
data class NavigatorChooseCity(
    val userId: Long,
    val email: String,
    val credentialTypes : UsersCredentialTypes,
    val firstName: String? = null,
    val lastName: String? = null,
    val photoUrl: String? = null,
): NavigatorConfig()
data class NavigatorVerifyEmailAddress(
    val userId: Long,
    val email: String,
    val cityId: Long
): NavigatorConfig()

object NavigatorProfilePage: NavigatorConfig()

object BSNavigatorChooseTakePhotoOrPickFile: UIBottomSheetConfig()
data class BSNavigatorTextInput(val visualModel: TextInputBSVisualModel): UIBottomSheetConfig()
data class BSNavigatorDualTextInput(val visualModel: DualTextInputBSVisualModel): UIBottomSheetConfig()
data class BSNavigatorCitySelection(val initialCityName: String, val onCitySelected: (cityId: Long, cityName: String) -> Unit): UIBottomSheetConfig()
data class BSNavigatorPhoneInput(val initialDialCode: String, val initialPhoneNumber: String, val onPhoneSelected: (String, String) -> Unit): UIBottomSheetConfig()
/** Language selection (e.g. Bulgarian / English) from Settings screen. */
data class BSNavigatorLanguageSelection(val currentLanguageCode: String, val onLanguageSelected: (String) -> Unit): UIBottomSheetConfig()
object BSNavigationRateApp: UIBottomSheetConfig()

/** Opens the "Filter results" screen from Home (filter/settings button). */
object BSNavigationFilterResults: UIBottomSheetConfig()

class BSFilter(val title: String? = null, val buttonText: String? = null, val list: List<FilterBottomSheetVisualModel>): UIBottomSheetConfig()

/** BOTTOM SHEET CHILD */

class ChooseTakePhotoOrPickFileBottomSheetChild(
    val title                   : String,
    val infoText                : List<LinkModel>?,
    val onInfoButtonClicked     : () -> Unit,
    val additionalRows          : List<ChooseTakePhotoOrPickFileVisualModel>? = null
): BottomSheetChild()

class TextInputBottomSheetChild(
    override val viewModel: TextInputBottomSheetViewModel
): BottomSheetChild(
    viewModel = viewModel,
    showNotch = false,
    removeBottomSheetPadding = true,
    useWebDialogInsets = true
)

class DualTextInputBottomSheetChild(
    override val viewModel: DualTextInputBottomSheetViewModel
): BottomSheetChild(
    viewModel = viewModel,
    showNotch = false,
    removeBottomSheetPadding = true,
    useWebDialogInsets = true
)
class RateAppBottomSheetChild(val onLaterClicked: () -> Unit, val onRateUsClicked: () -> Unit): BottomSheetChild()
class FilterBottomSheetChild(override val viewModel: FilterBottomSheetViewModel): BottomSheetChild()

/** DIALOGS */

data class DialogNavigatorStandard(
    override var title          : String?               = null,
    override val message        : String                = "",
    override val clickableText  : List<LinkModel>?      = null,
    override val coloredMessage : List<ColoredText>?    = null,
    override val positiveTitle  : String,
    override val negativeTitle  : String?               = null,
    override val closable       : Boolean               = true,
    /** When true, uses Figma Log out layout: stacked full-width buttons, 348dp width, 40dp radius, 32/28 padding. */
    val stackedButtons          : Boolean               = false,
): DialogConfig()

class DialogNavigatorUpdate(override val closable: Boolean = true): DialogConfig() {
    override val title          = sharedString(SR.strings.new_update_is_available)
    override val message        = sharedString(SR.strings.it_looks_like_you_have_an_older_version)
    override val positiveTitle  = sharedString(SR.strings.update)
    override val negativeTitle  = sharedString(SR.strings.cancel)
}

object DialogNavigatorForceUpdate: DialogConfig() {
    override val title          = sharedString(SR.strings.mystybag_new_version)
    override val message        = sharedString(SR.strings.update_message)
    override val positiveTitle  = sharedString(SR.strings.update)
    override val negativeTitle  = null
    override val closable       = false
}